import type { ReactNode } from 'react';
import { Icon } from '@/components/ui/icon';
import { focusRing } from '@/components/ui/focus-ring';
import styles from './deep-dive-header.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

/** One active filter, surfaced as a removable chip. */
export interface DeepDiveChip {
  /** Stable identity passed back to `onClearChip`. */
  key: string;
  /** Visible chip text (already localised by the caller). */
  label: string;
}

export interface DeepDiveHeaderProps {
  /** Section title, e.g. "Weeknight dinners". */
  title: ReactNode;
  /** Fired when the back affordance is pressed. */
  onBack: () => void;
  /** Text of the back-to-dashboard button. */
  backLabel: string;
  /** Optional result count. Omit to hide the count line. */
  count?: number;
  /** Unit word appended after the count, e.g. "recipes". */
  countUnitLabel?: string;
  /** Human-readable name of the active sort; omit to hide the sort summary. */
  sortLabel?: string;
  /** Prefix shown before the sort name, e.g. "Sorted:". */
  sortedLabel?: string;
  /** Sort direction — drives the arrow glyph. */
  dir?: 'asc' | 'desc';
  /** Active-filter chips. Omit or pass `[]` to hide the chip row. */
  chips?: DeepDiveChip[];
  /** Fired with a chip's `key` when its ✕ is pressed. */
  onClearChip?: (key: string) => void;
  /** When provided, renders a "Clear all" action after the chips. */
  onClearAll?: () => void;
  /** Text of the "Clear all" action. */
  clearAllLabel?: string;
  className?: string;
}

/**
 * The header a "See all" section deep-dive opens with: a back-to-dashboard
 * affordance, an optional result count, the section title, an optional sort
 * summary, and a row of active-filter chips (each removable) plus an
 * optional "Clear all". Purely presentational — all copy arrives as props.
 */
export function DeepDiveHeader({
  title,
  onBack,
  backLabel,
  count,
  countUnitLabel,
  sortLabel,
  sortedLabel,
  dir = 'desc',
  chips = [],
  onClearChip,
  onClearAll,
  clearAllLabel,
  className,
}: DeepDiveHeaderProps) {
  return (
    <header className={cx(styles.root, className)}>
      <div className={styles.top}>
        <button type="button" className={cx(styles.back, focusRing)} onClick={onBack}>
          <Icon name="arrow-left" size={16} />
          {backLabel}
        </button>
        {count != null ? (
          <span className={styles.count}>
            {count} {countUnitLabel}
          </span>
        ) : null}
      </div>
      <div className={styles.row}>
        <h1 className={styles.title}>{title}</h1>
        {sortLabel ? (
          <span className={styles.sort}>
            <Icon name="sort" size={14} />
            {sortedLabel} <b>{sortLabel}</b>
            <Icon name={dir === 'asc' ? 'arrow-up' : 'arrow-down'} size={13} />
          </span>
        ) : null}
      </div>
      {chips.length ? (
        <div className={styles.chips}>
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={cx(styles.chip, focusRing)}
              onClick={() => onClearChip?.(chip.key)}
            >
              {chip.label}
              <Icon name="x" size={13} />
            </button>
          ))}
          {onClearAll ? (
            <button
              type="button"
              className={cx(styles.clear, focusRing)}
              onClick={onClearAll}
            >
              {clearAllLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
